import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useRole, useTier } from '@/stores/identity';
import { getOwnerBannerRecipients, listOwnerBanners, type BannerRecipient } from '@/lib/banners';
import type { Banner } from '@/lib/types';
import { humanize } from '@/lib/errors';
import { formatDate } from '@/lib/dates';

/**
 * /owner/banners/:id/recipients — Enterprise-only read-receipt report.
 * One row per active staff member with their acknowledgement timestamp,
 * or "Pending" if they haven't acknowledged yet.
 */
export default function OwnerBannerRecipients() {
  const role = useRole();
  const tier = useTier();
  const isOwner = role === 'owner';
  const isEnterprise = tier === 'enterprise';
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [banner, setBanner] = useState<Banner | null>(null);
  const [rows, setRows] = useState<BannerRecipient[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isOwner || !id) return;
    let cancelled = false;
    Promise.all([listOwnerBanners(), getOwnerBannerRecipients(id)])
      .then(([all, recips]) => {
        if (cancelled) return;
        const b = all.find((x) => x.id === id);
        setBanner(b ?? null);
        setRows(recips);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(humanize(err));
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [isOwner, id]);

  if (!isOwner) return <div className="container mx-auto py-8 px-4"><p className="text-sm text-muted-foreground">Owner only.</p></div>;
  if (!isEnterprise) {
    return (
      <div className="container mx-auto py-8 px-4">
        <div className="max-w-2xl mx-auto rounded-lg border border-border bg-card p-6 space-y-3">
          <h1 className="text-2xl font-semibold tracking-tight">Read receipts</h1>
          <p className="text-sm">Acknowledgement reports are available on <strong>Enterprise</strong>.</p>
          <Link to="/billing" className="inline-block rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground">Upgrade</Link>
        </div>
      </div>
    );
  }
  if (loading) return <p className="container mx-auto py-8 px-4 text-sm text-muted-foreground">Loading…</p>;
  if (error)   return <p className="container mx-auto py-8 px-4 text-sm text-red-600">{error}</p>;
  if (!banner) return <p className="container mx-auto py-8 px-4 text-sm text-muted-foreground">Banner not found.</p>;

  const acked = rows.filter((r) => r.acknowledged_at != null);
  const pending = rows.filter((r) => r.acknowledged_at == null);
  const ackRate = rows.length === 0 ? 0 : Math.round((acked.length / rows.length) * 100);

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="space-y-1">
          <button onClick={() => navigate('/owner/banners')} className="text-xs text-muted-foreground hover:underline">← Back to communications</button>
          <h1 className="text-2xl font-semibold tracking-tight">Read receipts</h1>
          <p className="text-sm text-muted-foreground">
            <strong>{banner.title}</strong>
          </p>
        </div>

        <section className="grid grid-cols-3 gap-3">
          <Tile label="Acknowledged" value={String(acked.length)} accent="emerald" />
          <Tile label="Pending" value={String(pending.length)} accent={pending.length > 0 ? 'amber' : 'muted'} />
          <Tile label="Ack rate" value={`${ackRate}%`} accent={ackRate >= 80 ? 'emerald' : ackRate >= 50 ? 'amber' : 'red'} />
        </section>

        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">No recipients in your org yet.</p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-border bg-card">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">Name</th>
                  <th className="px-3 py-2 text-left">Role</th>
                  <th className="px-3 py-2 text-left">Email</th>
                  <th className="px-3 py-2 text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.user_id} className="border-t border-border">
                    <td className="px-3 py-2 font-medium">{r.display_name}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground uppercase tracking-wide">{r.role}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{r.email}</td>
                    <td className="px-3 py-2">
                      {r.acknowledged_at ? (
                        <span className="inline-flex items-center gap-1 text-xs text-emerald-700">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                          {formatDate(r.acknowledged_at)}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs text-amber-700">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                          Pending
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <p className="text-xs text-muted-foreground">
          This list reflects active staff at the time of viewing. Staff who joined after the
          banner was published will appear as Pending until they acknowledge.
        </p>
      </div>
    </div>
  );
}

function Tile({ label, value, accent }: { label: string; value: string; accent: 'emerald' | 'amber' | 'red' | 'muted' }) {
  const color =
    accent === 'emerald' ? 'text-emerald-700' :
    accent === 'amber'   ? 'text-amber-700'   :
    accent === 'red'     ? 'text-red-700'     :
                           'text-foreground';
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`text-3xl font-semibold mt-1 ${color}`}>{value}</div>
    </div>
  );
}
